import React from 'react';

export type DynamicTokenContext = {
  inCity: string | null;
};

export type DynamicTokenRenderers = {
  inCity?: (value: string | null) => React.ReactNode;
};

// Default renderer for the {{inCity}} token.
function defaultInCityRenderer(value: string | null): React.ReactNode {
  if (!value) {
    return '?';
  }

  return ` in ${value}?`;
}

// Main function to render dynamic tokens within text.
export function renderDynamicToken(
  token: string,
  context: DynamicTokenContext,
  renderers: DynamicTokenRenderers = {},
): React.ReactNode {
  switch (token) {
    case '{{inCity}}':
      return (renderers.inCity ?? defaultInCityRenderer)(context.inCity);

    default:
      return token;
  }
}

// Helper function to parse nodes and handle line breaks.
function nodeParser(nodes: React.ReactNode[]): React.ReactNode[] {
  const result: React.ReactNode[] = [];

  nodes.forEach((node, nodeIndex) => {
    if (typeof node === 'string') {
      const lines = node.split(/\r?\n/);

      lines.forEach((line, lineIndex) => {
        if (line) {
          result.push(
            <React.Fragment key={`text-${nodeIndex}-${lineIndex}`}>{line}</React.Fragment>,
          );
        }

        if (lineIndex < lines.length - 1) {
          result.push(<br key={`br-${nodeIndex}-${lineIndex}`} />);
        }
      });

      return;
    }

    result.push(<React.Fragment key={`node-${nodeIndex}`}>{node}</React.Fragment>);
  });

  return result;
}

// Main function to tokenize dynamic text based on the provided context and renderers.
export function tokenizeDynamicText(
  text: string,
  context: DynamicTokenContext,
  renderers: DynamicTokenRenderers = {},
): React.ReactNode[] {
  const normalizedText = text.replace(/\s*{{inCity}}/g, '{{inCity}}');
  const tokenPattern = /({{inCity}})/g;
  const parts = normalizedText.split(tokenPattern);

  const tokenizedNodes = parts.map((part) => {
    if (part === '{{inCity}}') {
      return renderDynamicToken(part, context, renderers);
    }

    return part;
  });

  return nodeParser(tokenizedNodes);
}

// Utility function to check if a value contains a specific token, supporting nested structures.
export function containsTokenDeep(value: unknown, token: string): boolean {
  if (typeof value === 'string') {
    return value.includes(token);
  }

  if (Array.isArray(value)) {
    return value.some((item) => containsTokenDeep(item, token));
  }

  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some((item) =>
      containsTokenDeep(item, token),
    );
  }

  return false;
}
